import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useNotification } from '../../context/NotificationContext';
import './Auth.css';

const API_URL = process.env.REACT_APP_API_URL;

const NAME_PATTERN = /^[\p{L}][\p{L}\s'’.-]*$/u;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EyeIcon = ({ off }) => (
  <svg className="auth-eye" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {off ? (
      <>
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
        <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </>
    ) : (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </>
    )}
  </svg>
);

function Register() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Kayıt başarılı olunca doğrulama e-postasının gönderildiği adres
  const [sentTo, setSentTo] = useState(null);
  const [isResending, setIsResending] = useState(false);

  const { showNotification } = useNotification();

  // Parola kuralları (sunucudaki kurallarla aynı)
  const rules = [
    { id: 'length', label: 'En az 8 karakter', ok: password.length >= 8 },
    { id: 'letter', label: 'En az bir harf', ok: /\p{L}/u.test(password) },
    { id: 'digit', label: 'En az bir rakam', ok: /\d/.test(password) },
  ];

  const validate = () => {
    const first = firstName.trim();
    const last = lastName.trim();
    if (first.length < 2 || first.length > 50 || !NAME_PATTERN.test(first)) {
      return 'Ad en az 2 karakter olmalı ve yalnızca harf içermelidir.';
    }
    if (last.length < 2 || last.length > 50 || !NAME_PATTERN.test(last)) {
      return 'Soyad en az 2 karakter olmalı ve yalnızca harf içermelidir.';
    }
    if (!EMAIL_PATTERN.test(email.trim())) return 'Geçerli bir e-posta adresi girin.';
    if (!rules.every((r) => r.ok)) return 'Parola belirtilen kuralları karşılamalı.';
    if (password.length > 72) return 'Parola en fazla 72 karakter olabilir.';
    if (password !== confirmPassword) return 'Parolalar eşleşmiyor.';
    return null;
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    const validationError = validate();
    if (validationError) {
      showNotification(validationError, 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/users/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          password,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        showNotification(data.error || 'Kayıt başarısız!', 'error');
        return;
      }

      // Hesap doğrulanana kadar giriş yapılamaz; kullanıcıya "e-postanı kontrol et" ekranı gösterilir
      setSentTo(email.trim());
    } catch (err) {
      console.error('Kayıt hatası:', err);
      showNotification('Sunucuya bağlanılamadı.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    setIsResending(true);
    try {
      const res = await fetch(`${API_URL}/api/users/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: sentTo }),
      });
      const data = await res.json();
      showNotification(data.message || data.error, res.ok ? 'success' : 'error');
    } catch (err) {
      console.error('Yeniden gönderme hatası:', err);
      showNotification('Sunucuya bağlanılamadı.', 'error');
    } finally {
      setIsResending(false);
    }
  };

  // Göz butonu: iki parola alanını birlikte açıp kapatır
  const eyeButton = (
    <button
      type="button"
      className="auth-toggle"
      onMouseDown={(e) => e.preventDefault()} /* imleç alanda kalsın */
      onClick={() => setShowPassword((v) => !v)}
      aria-label={showPassword ? 'Parolayı gizle' : 'Parolayı göster'}
      aria-pressed={showPassword}
      title={showPassword ? 'Parolayı gizle' : 'Parolayı göster'}
    >
      <EyeIcon off={showPassword} />
    </button>
  );

  // Kayıt sonrası onay ekranı
  if (sentTo) {
    return (
      <div className="auth-page">
        <main className="auth-main">
          <div className="auth">
            <span className="auth-kicker">Kayıt</span>
            <h1>E-postanızı kontrol edin</h1>
            <p className="auth-sub">
              <strong>{sentTo}</strong> adresine bir doğrulama bağlantısı gönderdik. Hesabınızı
              etkinleştirmek için bağlantıya tıklayın (24 saat geçerli). Spam klasörünü de kontrol edin.
            </p>
            <button
              className="auth-button"
              type="button"
              onClick={handleResend}
              disabled={isResending}
            >
              {isResending ? 'Gönderiliyor...' : 'Bağlantıyı tekrar gönder'}
            </button>
            <p className="auth-footer">
              <Link to="/login">Girişe dön</Link>
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="auth-page">
      {/* Sol bilgi alanı (yalnızca geniş ekranlarda) */}
      <aside className="auth-aside" aria-hidden="true">
        <span className="auth-aside-brand">EduConnect</span>
        <div>
          <h2>Birlikte öğrenmeye <em>başlayın.</em></h2>
          <p>Hesabınızı oluşturun, çalışma gruplarına katılın ve notlarınızı arkadaşlarınızla paylaşın.</p>
          <ul>
            <li>Ders, proje ve sınav grupları</li>
            <li>Grup sohbeti ve kaynak havuzu</li>
            <li>Etkinlik takvimi</li>
          </ul>
        </div>
      </aside>

      <main className="auth-main">
        <div className="auth">
          <span className="auth-kicker">Kayıt</span>
          <h1>Hesap oluşturun</h1>
          <p className="auth-sub">Birkaç bilgiyle EduConnect'e katılın.</p>

          <form className="auth-form" onSubmit={handleRegister} noValidate>
            <div className="auth-grid-2">
              <label className="auth-field">
                <span className="auth-label">Ad</span>
                <input
                  type="text"
                  className="auth-input"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  autoComplete="given-name"
                  maxLength={50}
                  required
                />
              </label>
              <label className="auth-field">
                <span className="auth-label">Soyad</span>
                <input
                  type="text"
                  className="auth-input"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  autoComplete="family-name"
                  maxLength={50}
                  required
                />
              </label>
            </div>

            <label className="auth-field">
              <span className="auth-label">E-posta</span>
              <input
                type="email"
                className="auth-input"
                placeholder="ornek@mail.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </label>

            <div className="auth-field">
              <label className="auth-label" htmlFor="register-password">Parola</label>
              <span className="auth-password">
                <input
                  id="register-password"
                  type={showPassword ? 'text' : 'password'}
                  className="auth-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  maxLength={72}
                  required
                />
                {eyeButton}
              </span>
              <ul className="auth-rules" aria-label="Parola kuralları">
                {rules.map((r) => (
                  <li key={r.id} className={r.ok ? 'is-ok' : ''}>{r.label}</li>
                ))}
              </ul>
            </div>

            <div className="auth-field">
              <label className="auth-label" htmlFor="register-password-confirm">Parola (tekrar)</label>
              <span className="auth-password">
                <input
                  id="register-password-confirm"
                  type={showPassword ? 'text' : 'password'}
                  className="auth-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  maxLength={72}
                  required
                />
                {eyeButton}
              </span>
              {confirmPassword && password !== confirmPassword && (
                <span className="auth-field-error">Parolalar eşleşmiyor.</span>
              )}
            </div>

            <button className="auth-button" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Kayıt yapılıyor...' : 'Kayıt ol'}
            </button>
          </form>

          <p className="auth-footer">
            Hesabınız var mı? <Link to="/login">Giriş yap</Link>
          </p>
        </div>
      </main>
    </div>
  );
}

export default Register;